import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { legacyContent as c } from "../mock";

const eraImages = [c.images.stonePath, c.images.diningTable, c.images.ledger, null];

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const MAP_PROJECTION_CONFIG = { scale: 210, center: [30, 28] };

const CITY_COORDS = {
  "Antalya, Turkey": [30.7, 36.9],
  "Barcelona, Spain": [2.2, 41.4],
  "Greater Noida, India": [77.5, 28.5],
  "Istanbul, Turkey": [29.0, 41.0],
  Monaco: [7.4, 43.7],
  Malaysia: [101.7, 3.1],
  Mauritius: [57.5, -20.2],
  Muscat: [58.4, 23.6],
  "Abu Dhabi": [54.4, 24.5],
  Switzerland: [8.5, 47.4],
  Venice: [12.33, 45.44],
  Ibiza: [1.43, 38.91],
  Austria: [16.4, 48.2],
  Venice: [12.33, 45.44],
  Vienna: [16.37, 48.21],
  Salzburg: [13.05, 47.81],
  Montreux: [6.91, 46.43],
  "Kota Kinabalu": [116.07, 5.98],
  "Hua Hin": [99.96, 12.57],
  Phuket: [98.34, 7.88],
  Florence: [11.26, 43.77],
  Lisbon: [-9.14, 38.72],
  Cannes: [7.02, 43.55],
  "New Delhi": [77.21, 28.61],
  Mumbai: [72.87, 19.07],
  Kolkata: [88.36, 22.57],
  Bengaluru: [77.59, 12.97],
  Hyderabad: [78.47, 17.38],
  Jaipur: [75.79, 26.91],
  Udaipur: [73.71, 24.58],
  Ahmedabad: [72.57, 23.02],
  Goa: [73.87, 15.5],
  Lucknow: [80.95, 26.85],
  Amritsar: [74.87, 31.63],
  Kochi: [76.27, 9.93],
  Chandigarh: [76.78, 30.73],
  Pune: [73.86, 18.52],
  Srinagar: [74.8, 34.08],
  Bhubaneswar: [85.82, 20.3],
  Indore: [75.86, 22.72],
  Ludhiana: [75.85, 30.9],
  Raipur: [81.63, 21.25],
  Bareilly: [79.42, 28.36],
  Mahabaleshwar: [73.66, 17.92],
  Agra: [78.01, 27.18],
  Surat: [72.83, 21.17],
  Baroda: [73.19, 22.31],
  Coonoor: [76.79, 11.35],
  Beawar: [74.32, 26.1],
  Ajmer: [74.64, 26.45],
  Gulmarg: [74.38, 34.05],
  Mussoorie: [78.08, 30.46],
  Dehradun: [78.03, 30.32],
  Budapest: [19.04, 47.5],
  Jaisalmer: [70.9, 26.91],
  "Phu Quoc, Vietnam": [103.98, 10.22],
  Ranthambore: [76.4, 26.02],
};

const JOURNAL_LINKS = {
  Venice: "a-wedding-carried-across-the-water-venice",
  Monaco: "a-table-on-the-riviera-monaco",
  Lisbon: "a-fairy-tale-in-a-bubble-lisbon",
  Ibiza: "the-island-supplied-by-sea-ibiza",
  "Phu Quoc, Vietnam": "at-jw-marriott-phu-quoc",
  Ranthambore: "a-toast-in-ranthambore",
  Gulmarg: "chole-bhature-at-2800-metres-kashmir",
  Coonoor: "the-lean-mean-team-coonoor",
};

const WorldMap = () => {
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const activeLink = active ? JOURNAL_LINKS[active] : null;
  return (
    <Reveal className="mt-16 max-w-4xl mx-auto">
      <div className="relative w-full bg-cover-ivory border border-hairline-beige overflow-visible">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={MAP_PROJECTION_CONFIG}
          style={{ width: "100%", height: "auto", overflow: "visible" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const isIndia = geo.properties.name === "India";
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isIndia ? "#6B6152" : "#E6DECD"}
                    stroke="#F7F3EA"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: "none" },
                      hover: {
                        outline: "none",
                        fill: isIndia ? "#6B6152" : "#DED4BE",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
          {c.foodprints.milestones.map((m) => {
            const coords = CITY_COORDS[m.city];
            if (!coords) return null;
            const link = JOURNAL_LINKS[m.city];
            return (
              <Marker
                key={m.city}
                coordinates={coords}
                onMouseEnter={() => setActive(m.city)}
                onMouseLeave={() => setActive(null)}
                onClick={() => link && navigate(`/journal/${link}`)}
                data-testid={link ? `map-pin-link-${link}` : undefined}
                style={{ default: { cursor: link ? "pointer" : "default" } }}
              >
                <g transform="translate(-6, -12)">
                  {link && (
                    <circle
                      cx="6"
                      cy="6"
                      r="5"
                      fill="none"
                      stroke="#C9A85C"
                      strokeWidth="0.7"
                      opacity="0.75"
                    />
                  )}
                  <path
                    d="M6 0C2.7 0 0 2.7 0 6c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6z"
                    fill="#C9A85C"
                    stroke="#28241F"
                    strokeWidth={0.4}
                  />
                  <circle cx="6" cy="6" r="2.2" fill="#28241F" />
                </g>
              </Marker>
            );
          })}
          {active && CITY_COORDS[active] && (
            <Marker
              coordinates={CITY_COORDS[active]}
              style={{ default: { pointerEvents: "none" } }}
            >
              <g transform="translate(-7, -14)">
                <path
                  d="M7 0C3.1 0 0 3.1 0 7c0 5.25 7 14 7 14s7-8.75 7-14c0-3.9-3.1-7-7-7z"
                  fill="#28241F"
                  stroke="#C9A85C"
                  strokeWidth={0.7}
                />
                <circle cx="7" cy="7" r="2.6" fill="#C9A85C" />
              </g>
              <g transform="translate(0, -28)">
                <foreignObject
                  x="-95"
                  y="-46"
                  width="190"
                  height="48"
                  style={{ overflow: "visible" }}
                >
                  <div className="inline-block bg-brand-ink text-ink-on-dark text-center px-3 py-1.5 rounded-sm shadow-xl">
                    <div
                      style={{ fontFamily: "Cormorant Garamond" }}
                      className="text-[14px] leading-tight whitespace-nowrap"
                    >
                      {active}
                    </div>
                    <div className="font-inter text-[7px] uppercase tracking-[0.18em] text-bright-gold-on-dark leading-tight mt-0.5 whitespace-nowrap">
                      {c.foodprints.milestones.find((m) => m.city === active)?.event}
                    </div>
                  </div>
                </foreignObject>
              </g>
            </Marker>
          )}
        </ComposableMap>
      </div>

      {/* Active caption */}
      <div className="min-h-[23px] mt-6 text-center">
        {active ? (
          activeLink ? (
            <Link
              to={`/journal/${activeLink}`}
              data-testid="map-caption-read-note"
              className="group inline-flex items-center gap-2 font-inter text-label-sm uppercase tracking-widest text-brand-ink"
            >
              <span className="text-bright-gold-on-dark">{active}</span>
              <span>— Read the note</span>
              <ArrowRight className="w-4 h-4 text-brand-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          ) : (
            <p className="font-inter text-label-sm uppercase tracking-widest text-brand-ink">
              <span className="text-bright-gold-on-dark">{active}</span>
              {" — "}
              {c.foodprints.milestones.find((m) => m.city === active)?.event}
            </p>
          )
        ) : (
          <div className="flex items-center justify-center gap-2 text-deep-taupe">
            <MapPin className="w-4 h-4 text-brand-gold" />
            <span className="font-inter text-label-sm uppercase tracking-widest">
              Hover a marker — ringed pins open a story
            </span>
          </div>
        )}
      </div>
    </Reveal>
  );
};

const Legacy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header />

      {/* Hero — archival ivory */}
      <section className="pt-40 pb-24 px-margin-mobile md:px-margin-desktop">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal as="p" className="font-inter text-eyebrow text-brand-secondary uppercase mb-6">
            {c.chapter}
          </Reveal>
          <Reveal
            as="h1"
            delay={100}
            className="font-cormorant text-display-lg-mobile md:text-display-lg text-brand-ink mb-6"
          >
            {c.title}
          </Reveal>
          <Reveal
            as="p"
            delay={200}
            className="font-inter text-eyebrow text-deep-taupe uppercase mb-8"
          >
            {c.heroSubtitle}
          </Reveal>
          <Reveal delay={300} className="w-16 h-px bg-brand-gold mx-auto mb-8" />
          <Reveal
            as="p"
            delay={400}
            className="font-cormorant text-body-lg text-deep-taupe max-w-2xl mx-auto"
          >
            {c.intro}
          </Reveal>
        </div>
      </section>

      {/* A Legacy of Trust — stat row */}
      <section className="px-margin-mobile md:px-margin-desktop pb-narrative-gap">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <Reveal as="p" className="font-inter text-eyebrow uppercase tracking-[0.3em] text-brand-secondary mb-6">
              A Legacy of Trust
            </Reveal>
            <Reveal as="h2" delay={100} className="font-cormorant text-[27px] md:text-[42px] leading-tight text-brand-ink mb-6">
              Many have returned. Then returned again.
            </Reveal>
            <Reveal as="p" delay={200} className="font-cormorant text-body-lg text-deep-taupe">
              For nearly three decades, families have welcomed us into the most
              meaningful moments of their lives — introducing the next generation
              to the same philosophy of hospitality.
            </Reveal>
          </div>
        </div>
      </section>


      {/* Era narrative — alternating editorial blocks */}
      <section className="px-margin-mobile md:px-margin-desktop pb-narrative-gap flex flex-col gap-24 md:gap-narrative-gap">
        {c.eras.map((era, i) => {
          const image = eraImages[i];
          const imageLeft = i % 2 === 0;
          if (!image) {
            // Text-only centered statement (Today & The Future)
            return (
              <Reveal key={era.title} className="max-w-3xl mx-auto text-center">
                <p className="font-inter text-eyebrow text-brand-secondary uppercase mb-5">
                  {era.label}
                </p>
                <h2 className="font-cormorant text-headline-md text-brand-ink mb-6">
                  {era.title}
                </h2>
                <p className="font-cormorant text-body-lg text-deep-taupe">
                  {era.body}
                </p>
              </Reveal>
            );
          }
          return (
            <div
              key={era.title}
              className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-center"
            >
              <Reveal
                className={`col-span-1 md:col-span-7 ${
                  imageLeft ? "md:order-1" : "md:order-2 md:col-start-6"
                }`}
              >
                <div className="w-full aspect-[4/3] overflow-hidden group bg-cover-ivory">
                  <img
                    src={image}
                    alt={era.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </Reveal>
              <Reveal
                delay={120}
                className={`col-span-1 md:col-span-4 ${
                  imageLeft ? "md:order-2 md:col-start-9" : "md:order-1 md:col-start-1"
                }`}
              >
                <p className="font-inter text-eyebrow text-brand-secondary uppercase mb-4">
                  {era.label}
                </p>
                <h2 className="font-cormorant text-headline-md text-brand-ink mb-5">
                  {era.title}
                </h2>
                <p className="font-cormorant text-body-md text-deep-taupe">
                  {era.body}
                </p>
              </Reveal>
            </div>
          );
        })}
      </section>

      {/* Seal band — timeless coda over Sema footage */}
      <section className="relative bg-brand-ink px-margin-mobile md:px-margin-desktop pt-44 md:pt-56 pb-28 md:pb-36 overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-[0.6]"
          autoPlay
          muted
          loop
          playsInline
          poster="/timeless-sema-poster.jpg"
          data-testid="timeless-video"
        >
          <source src="/timeless-sema.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-brand-ink/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/85 via-transparent to-brand-ink/85" />
        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center text-center mt-16 md:mt-28">
          <Reveal>
            <span className="font-cormorant text-[21px] tracking-[0.18em] uppercase text-ink-on-dark opacity-90 mb-8 block">
              The Kitchen Art Co.
            </span>
          </Reveal>
          <Reveal
            as="h3"
            delay={120}
            className="font-cormorant text-headline-md text-ink-on-dark mb-10"
          >
            {c.sealBand.caption}
          </Reveal>
          <Reveal delay={220} className="flex items-center gap-5 md:gap-7 w-full justify-center">
            <span className="font-inter text-eyebrow text-bright-gold-on-dark uppercase tracking-[0.25em]">
              1997
            </span>
            <span className="draw-line h-px w-20 md:w-48 bg-bright-gold-on-dark/80" />
            <span className="font-inter text-eyebrow text-bright-gold-on-dark uppercase tracking-[0.25em]">
              2027
            </span>
          </Reveal>
          <Reveal
            as="p"
            delay={300}
            className="font-inter text-eyebrow uppercase tracking-widest mt-8"
          >
            <span className="time-shimmer">{c.sealBand.progress}</span>
          </Reveal>
        </div>
      </section>

      {/* Global Food-Prints — International Milestones */}
      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-3xl">
          <Reveal as="p" className="font-inter text-eyebrow text-brand-secondary uppercase mb-4">
            {c.foodprints.eyebrow}
          </Reveal>
          <Reveal
            as="h2"
            delay={100}
            className="font-cormorant text-display-lg-mobile text-brand-ink"
          >
            {c.foodprints.heading}
          </Reveal>
          <Reveal
            as="p"
            delay={180}
            className="font-inter text-eyebrow uppercase tracking-[0.28em] text-brand-secondary mt-6"
          >
            30 Years&nbsp;&middot;&nbsp;{c.foodprints.milestones.length} Destinations
          </Reveal>
        </div>

        {/* Interactive world map */}
        <WorldMap />
      </section>

      {/* Closing CTA */}
      <section className="bg-cover-ivory px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-gold px-8 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                Begin a Conversation
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Legacy;
